import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PhoneCall, AlertTriangle, Clock, CheckCircle2, MessageSquare, User, Send, Paperclip, X as XIcon, BookmarkPlus, FileText, Image as ImageIcon, Save, ChevronDown } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import AttachmentsPanel from "@/components/common/AttachmentsPanel";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function slaCountdown(deadlineIso) {
  if (!deadlineIso) return { label: "—", tone: "muted" };
  const ms = new Date(deadlineIso).getTime() - Date.now();
  if (ms <= 0) return { label: "Overdue", tone: "danger" };
  const min = Math.floor(ms / 60000);
  if (min < 60) return { label: `${min}m left`, tone: min < 5 ? "danger" : min < 15 ? "warn" : "ok" };
  const h = Math.floor(min / 60);
  return { label: `${h}h ${min % 60}m left`, tone: "ok" };
}

function UrgencyBadge({ urgency }) {
  const isUrgent = urgency === "urgent";
  return (
    <span
      data-testid={`callback-urgency-${urgency}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${
        isUrgent ? "bg-red-50 text-red-700 border border-red-200" : "bg-slate-50 text-slate-700 border border-slate-200"
      }`}
    >
      {isUrgent && <AlertTriangle className="w-3 h-3" />}
      {urgency}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-50 text-amber-700 border border-amber-200",
    acknowledged: "bg-blue-50 text-blue-700 border border-blue-200",
    completed: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    escalated: "bg-red-50 text-red-700 border border-red-200",
    cancelled: "bg-slate-100 text-slate-500 border border-slate-200",
  };
  return (
    <span data-testid={`callback-status-${status}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${map[status] || map.pending}`}>
      {status}
    </span>
  );
}

function humanKB(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FilePreviewChip({ file, onRemove, testId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (file.type?.startsWith("image/")) {
      const u = URL.createObjectURL(file);
      setUrl(u);
      return () => URL.revokeObjectURL(u);
    }
    setUrl(null);
  }, [file]);
  const ct = file.type || "";
  const isImage = ct.startsWith("image/");
  const isPdf = ct === "application/pdf";
  return (
    <div className="inline-flex items-start gap-2 rounded-lg border border-[#E5E7EB] bg-white p-1.5 pr-2" data-testid={testId}>
      <div className="w-12 h-12 rounded flex items-center justify-center bg-slate-50 border border-[#F1F5F9] overflow-hidden shrink-0">
        {isImage && url ? (
          <img src={url} alt={file.name} className="w-full h-full object-cover" />
        ) : isPdf ? (
          <div className="text-[10px] font-bold text-red-600">PDF</div>
        ) : ct.startsWith("audio/") ? (
          <div className="text-[10px] font-bold text-blue-600">AUDIO</div>
        ) : ct.startsWith("video/") ? (
          <div className="text-[10px] font-bold text-purple-600">VIDEO</div>
        ) : (
          <FileText className="w-5 h-5 text-slate-400" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[12px] text-[#1F2937] truncate max-w-[160px]" title={file.name}>{file.name}</div>
        <div className="text-[10px] text-[#9CA3AF]">{humanKB(file.size)} · {ct.split("/").pop() || "file"}</div>
      </div>
      {onRemove && (
        <button type="button" onClick={onRemove}
                className="text-[#9CA3AF] hover:text-red-600" title="Remove">
          <XIcon className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function AttachmentPicker({ files, setFiles, testIdPrefix }) {
  const inputRef = useRef(null);
  function onPick(e) {
    const arr = Array.from(e.target.files || []);
    const oversized = arr.find((f) => f.size > 20 * 1024 * 1024);
    if (oversized) { toast.error(`"${oversized.name}" exceeds 20 MB.`); e.target.value = ""; return; }
    setFiles([...files, ...arr]);
    e.target.value = "";
  }
  return (
    <div className="space-y-2">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">Attach documents (optional)</div>
      <div className="flex flex-wrap items-start gap-2">
        <input ref={inputRef} type="file" multiple className="hidden" onChange={onPick}
               data-testid={`${testIdPrefix}-file-input`} />
        <Button type="button" size="sm" variant="outline"
          onClick={() => inputRef.current?.click()}
          className="border-[#0B7E8C]/40 text-[#0B7E8C] hover:bg-[#0B7E8C]/5 h-9"
          data-testid={`${testIdPrefix}-pick-file-btn`}>
          <Paperclip className="w-3.5 h-3.5 mr-1" /> Add file
        </Button>
        {files.map((f, i) => (
          <FilePreviewChip key={i} file={f}
            onRemove={() => setFiles(files.filter((_, j) => j !== i))}
            testId={`${testIdPrefix}-file-chip-${i}`} />
        ))}
      </div>
      <div className="text-[10px] text-[#9CA3AF]">
        PDFs, images, Office docs, audio/video, text — up to 20 MB each. Patient will see anything you attach here.
      </div>
    </div>
  );
}

async function uploadCallbackFiles(cbId, files) {
  for (const f of files) {
    const fd = new FormData();
    fd.append("file", f);
    await api.post(`/callbacks/${cbId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  }
}

function TemplateBar({ testIdPrefix, notes, files, onApplyTemplate, onSavedTemplate }) {
  const [templates, setTemplates] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    api.get("/callbacks/templates")
      .then((r) => setTemplates(r.data.items || []))
      .catch(() => setTemplates([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  function pick(tid) {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    onApplyTemplate(t);
  }
  async function save() {
    const n = (name || "").trim();
    if (!n) { toast.error("Template name is required."); return; }
    if (!notes.trim() && files.length === 0) { toast.error("Add notes or at least one file before saving."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", n);
      fd.append("notes", notes || "");
      files.forEach((f) => fd.append("files", f));
      await api.post("/callbacks/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Template "${n}" saved`);
      setSaveOpen(false); setName("");
      load();
      onSavedTemplate && onSavedTemplate();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {templates.length > 0 && (
        <Select onValueChange={pick}>
          <SelectTrigger className="h-8 max-w-[220px] text-xs border-[#0B7E8C]/30" data-testid={`${testIdPrefix}-templates-trigger`}>
            <SelectValue placeholder="Use template…" />
          </SelectTrigger>
          <SelectContent>
            {templates.map((t) => (
              <SelectItem key={t.id} value={t.id} data-testid={`${testIdPrefix}-template-${t.id}`}>
                {t.name}{(t.attachments?.length) ? ` · ${t.attachments.length} file(s)` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Button type="button" size="sm" variant="ghost"
        onClick={() => setSaveOpen(true)}
        className="h-8 text-xs text-[#0B7E8C] hover:bg-[#0B7E8C]/5"
        data-testid={`${testIdPrefix}-save-template-btn`}>
        <BookmarkPlus className="w-3.5 h-3.5 mr-1" /> Save as template
      </Button>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm" data-testid={`${testIdPrefix}-save-template-dialog`}>
          <DialogHeader><DialogTitle>Save as reply template</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
              placeholder="e.g. Wound-care instructions"
              data-testid={`${testIdPrefix}-template-name-input`} />
            <div className="text-[11px] text-[#6B7280]">
              This will save the current response notes and any {files.length ? files.length + " attached file(s)" : "files you've attached"} as a private template you can reuse.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving}
              className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
              data-testid={`${testIdPrefix}-template-save-submit`}>
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AckDialog({ open, onOpenChange, callback, onDone }) {
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [appliedTemplateId, setAppliedTemplateId] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setNotes(""); setFiles([]); setAppliedTemplateId(null); } }, [open]);
  function applyTemplate(t) {
    if (notes && !window.confirm("Replace current notes with template notes?")) return;
    setNotes(t.notes || "");
    setAppliedTemplateId(t.id);
    toast.success(`Template "${t.name}" applied. Files will attach on submit.`);
  }
  async function submit() {
    if (!notes.trim()) { toast.error("Please add response notes to acknowledge."); return; }
    setBusy(true);
    try {
      if (appliedTemplateId) await api.post(`/callbacks/${callback.id}/apply-template/${appliedTemplateId}`);
      if (files.length) await uploadCallbackFiles(callback.id, files);
      await api.post(`/callbacks/${callback.id}/acknowledge`, { response_notes: notes.trim() });
      const totalFiles = files.length + (appliedTemplateId ? 1 : 0);
      toast.success(totalFiles ? `Callback acknowledged · ${files.length || "template"} file(s) shared` : "Callback acknowledged");
      onDone && onDone();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ack-dialog" className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Acknowledge callback</DialogTitle></DialogHeader>
        <p className="text-xs text-[#6B7280] -mt-2">
          Confirm to the patient that you've seen the request and share what you're going to do next. Attach a document (e.g. wound-care instructions, prescription photo) to send it back with your reply.
        </p>
        <TemplateBar testIdPrefix="ack" notes={notes} files={files} onApplyTemplate={applyTemplate} />
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Response notes</div>
          <Textarea rows={4} maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Calling the patient now — will confirm wound care instructions and rebook a home visit if needed."
            data-testid="ack-notes" />
          <div className="text-[11px] text-[#9CA3AF] mt-1">{notes.length}/1000</div>
        </div>
        <AttachmentPicker files={files} setFiles={setFiles} testIdPrefix="ack" />
        {appliedTemplateId && (
          <div className="text-[11px] text-[#0B7E8C] bg-[#E6F5F7] border border-[#B8E1E6] rounded px-2 py-1.5"
               data-testid="ack-template-applied-hint">
            Template files will be cloned onto this callback when you hit Acknowledge.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ack-submit">
            <Send className="w-4 h-4 mr-1.5" /> {busy ? "Sending…" : "Acknowledge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompleteDialog({ open, onOpenChange, callback, onDone }) {
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [appliedTemplateId, setAppliedTemplateId] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setNotes(""); setFiles([]); setAppliedTemplateId(null); } }, [open]);
  function applyTemplate(t) {
    if (notes && !window.confirm("Replace current notes with template notes?")) return;
    setNotes(t.notes || "");
    setAppliedTemplateId(t.id);
    toast.success(`Template "${t.name}" applied. Files will attach on submit.`);
  }
  async function submit() {
    setBusy(true);
    try {
      if (appliedTemplateId) await api.post(`/callbacks/${callback.id}/apply-template/${appliedTemplateId}`);
      if (files.length) await uploadCallbackFiles(callback.id, files);
      await api.post(`/callbacks/${callback.id}/complete`, { completion_notes: notes.trim() || null });
      const totalFiles = files.length + (appliedTemplateId ? 1 : 0);
      toast.success(totalFiles ? `Callback complete · ${files.length || "template"} file(s) shared` : "Callback marked complete");
      onDone && onDone();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="complete-dialog" className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Mark callback complete</DialogTitle></DialogHeader>
        <TemplateBar testIdPrefix="complete" notes={notes} files={files} onApplyTemplate={applyTemplate} />
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Completion notes (optional)</div>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Summarise outcome for the patient record."
            data-testid="complete-notes" />
        </div>
        <AttachmentPicker files={files} setFiles={setFiles} testIdPrefix="complete" />
        {appliedTemplateId && (
          <div className="text-[11px] text-[#0B7E8C] bg-[#E6F5F7] border border-[#B8E1E6] rounded px-2 py-1.5"
               data-testid="complete-template-applied-hint">
            Template files will be cloned onto this callback when you hit Mark complete.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="complete-submit">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : "Mark complete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CallbackCard({ cb, onAck, onComplete, isAdmin }) {
  const sla = slaCountdown(cb.sla_deadline_at);
  const toneClass = sla.tone === "danger" ? "text-red-700 bg-red-50 border-red-200"
    : sla.tone === "warn" ? "text-amber-700 bg-amber-50 border-amber-200"
    : sla.tone === "ok" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : "text-slate-500 bg-slate-50 border-slate-200";
  return (
    <div className="hah-card p-4" data-testid={`callback-card-${cb.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <UrgencyBadge urgency={cb.urgency} />
            <StatusBadge status={cb.status} />
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border ${toneClass}`}
              data-testid={`callback-sla-${cb.id}`}>
              <Clock className="w-3 h-3" /> {sla.label}
            </span>
          </div>
          <div className="mt-1.5 text-sm font-semibold text-[#1F2937] flex items-center gap-1.5">
            <User className="w-4 h-4 text-[#6B7280]" /> {cb.patient_name || "Patient"}
            {cb.patient_phone && (
              <a href={`tel:${cb.patient_phone}`}
                 onClick={(e) => e.stopPropagation()}
                 className="inline-flex items-center gap-1 text-[11px] font-medium text-[#0B7E8C] hover:text-[#128C97] bg-[#E6F5F7] hover:bg-[#D5EEF1] rounded-full px-2 py-[2px]"
                 title="Call patient"
                 data-testid={`callback-patient-phone-${cb.id}`}>
                <PhoneCall className="w-3 h-3" />
                <span>{cb.patient_phone}</span>
              </a>
            )}
          </div>
          <div className="text-[11px] text-[#9CA3AF] mt-0.5">
            Preferred: <span className="font-medium">{ { stat: "STAT", morning: "Morning", afternoon: "Afternoon", evening: "Evening", second_day: "Second Day" }[cb.preferred_time] || (cb.preferred_time || "").toUpperCase()}</span>
            {"  ·  "}Requested {timeAgo(cb.created_at)}
          </div>
          <div className="mt-2 text-sm text-[#374151] whitespace-pre-wrap">{cb.reason}</div>
          {cb.response_notes && (
            <div className="mt-2 rounded-md bg-blue-50 border border-blue-100 text-[13px] text-blue-900 p-2">
              <span className="text-[11px] uppercase tracking-wider text-blue-700/70 mr-1">Your response:</span>
              {cb.response_notes}
            </div>
          )}
          <div className="mt-3">
            <AttachmentsPanel
              entityLabel="this callback"
              listUrl={`/callbacks/${cb.id}/attachments`}
              deleteUrlFor={(a) => (isAdmin && a.uploaded_by_kind !== "patient" ? `/callbacks/${cb.id}/attachments/${a.id}` : null)}
              testIdPrefix={`cb-att-${cb.id}`}
              compact
            />
          </div>
        </div>
        <div className="shrink-0 flex flex-col gap-1.5">
          {cb.status === "pending" && (
            <Button size="sm" onClick={() => onAck(cb)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white h-8"
              data-testid={`callback-ack-btn-${cb.id}`}>
              <MessageSquare className="w-4 h-4 mr-1" /> Acknowledge
            </Button>
          )}
          {(cb.status === "pending" || cb.status === "acknowledged") && (
            <Button size="sm" variant="outline" onClick={() => onComplete(cb)} className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid={`callback-complete-btn-${cb.id}`}>
              <CheckCircle2 className="w-4 h-4 mr-1" /> Complete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const STATUS_TABS = [
  { key: "open",         label: "Open",         statuses: "pending,acknowledged", emptyCopy: "Nothing open — you're all caught up." },
  { key: "pending",      label: "Pending",      statuses: "pending",              emptyCopy: "No pending callbacks." },
  { key: "acknowledged", label: "Acknowledged", statuses: "acknowledged",         emptyCopy: "No acknowledged callbacks awaiting completion." },
  { key: "completed",    label: "Completed",    statuses: "completed",            emptyCopy: "No completed callbacks yet." },
  { key: "escalated",    label: "Escalated",    statuses: "escalated",            emptyCopy: "No escalated callbacks." },
  { key: "cancelled",    label: "Cancelled",    statuses: "cancelled",            emptyCopy: "No cancelled callbacks." },
  { key: "all",          label: "All",          statuses: "pending,acknowledged,completed,escalated,cancelled", emptyCopy: "No callback requests." },
];

export default function MyCallbacks() {
  const [items, setItems] = useState(null);
  const [ackTarget, setAckTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [tab, setTab] = useState("open");
  const [, force] = useState(0);
  const { user } = useAuth();
  const isAdmin = user?.role_code === "admin";

  const currentTab = useMemo(() => STATUS_TABS.find((t) => t.key === tab) || STATUS_TABS[0], [tab]);

  const load = useCallback(() => {
    setItems(null);
    api.get("/callbacks/mine", { params: { status: currentTab.statuses } })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => { toast.error(formatApiError(e)); setItems([]); });
  }, [currentTab.statuses]);
  useEffect(() => { load(); }, [load]);

  // Live SLA countdown — tick every 30s
  useEffect(() => {
    const t = setInterval(() => force((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // Split into pending / acknowledged / other buckets for readable grouping when applicable.
  const sortedItems = useMemo(() => {
    const arr = [...(items || [])];
    // Urgent first, then newest first
    arr.sort((a, b) => {
      if (a.urgency !== b.urgency) return a.urgency === "urgent" ? -1 : 1;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return arr;
  }, [items]);

  return (
    <div data-testid="my-callbacks-page">
      <PageHeader
        title="Callback Requests"
        subtitle="Patients waiting for you to call them back. Acknowledge within the SLA to avoid escalation."
        icon={PhoneCall}
      />

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="callbacks-status-filter">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`callbacks-status-${t.key}`}
            className={`px-3 py-1 rounded-full border text-[12px] font-medium transition-colors ${
              tab === t.key
                ? "border-[#0B7E8C] bg-[#0B7E8C]/10 text-[#0B7E8C]"
                : "border-[#E5E7EB] text-[#6B7280] hover:border-[#0B7E8C]/50"
            }`}
          >
            {t.label}
            {tab === t.key && items !== null ? (
              <span className="ms-1 opacity-70">({items.length})</span>
            ) : null}
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="text-sm text-[#6B7280]" data-testid="callbacks-loading">Loading…</div>
      ) : sortedItems.length === 0 ? (
        <div className="hah-card p-6 text-sm text-[#6B7280]" data-testid="callbacks-empty">
          {currentTab.emptyCopy}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3" data-testid="callbacks-list">
          {sortedItems.map((cb) => (
            <CallbackCard key={cb.id} cb={cb} onAck={setAckTarget} onComplete={setCompleteTarget} isAdmin={isAdmin} />
          ))}
        </div>
      )}

      {ackTarget && <AckDialog open={!!ackTarget} onOpenChange={(o) => !o && setAckTarget(null)} callback={ackTarget} onDone={load} />}
      {completeTarget && <CompleteDialog open={!!completeTarget} onOpenChange={(o) => !o && setCompleteTarget(null)} callback={completeTarget} onDone={load} />}
    </div>
  );
}
